import { Button, Checkbox, Group, Modal, Pagination, ScrollArea, Stack, Text, Tooltip } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { IconCheck, IconMessageCircle } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { SvgDataset } from '@/components/Embeddings/icon';
import {
  GbaseProject,
  GbasePaginator,
  GetGbaseProjectsResponse,
  gbaseAuthorize,
  getGbaseProjects,
  getGbaseAuthStatus,
  associateGbaseProjects,
  disassociateGbaseProject,
} from '@mygpt/app/apis';
import { useIntegrationRuleStore } from '@/pages/Datasets/Integration/Tasks/useIntegrationRuleStore';

interface GbaseConnectModalProps {
  opened: boolean;
  onClose: () => void;
  datasetId: string;
}

// 弹窗状态类型
type ModalStatus = 'loading' | 'no_auth' | 'has_data_not_selected' | 'has_data_selected';

// 分页配置
const PAGE_SIZE = 10;

export const GbaseConnectModal: React.FC<GbaseConnectModalProps> = ({ opened, onClose, datasetId }) => {
  const { t } = useTranslation();
  const { datasetId: routeDatasetId, id: routeBotId } = useParams<{ datasetId: string; id: string }>();
  const { fetchRules } = useIntegrationRuleStore();

  // 弹窗状态
  const [status, setStatus] = useState<ModalStatus>('loading');

  // Project 列表数据
  const [projects, setProjects] = useState<GbaseProject[]>([]);

  // 分页信息
  const [paginator, setPaginator] = useState<GbasePaginator>({
    currentPage: 1,
    items: 0,
    pageSize: PAGE_SIZE,
    pages: 1,
    startRow: 0,
    endRow: 0,
  });

  // 已关联的 Project
  const [associatedProject, setAssociatedProject] = useState<GbaseProject | null>(null);

  // 选择的项目 UUID（用于"有数据未选择"状态）
  const [selectedUuid, setSelectedUuid] = useState<string>('');

  // 加载状态
  const [loading, setLoading] = useState(false);

  // 删除确认弹窗
  const [deleteConfirmOpened, setDeleteConfirmOpened] = useState(false);

  // 加载数据
  const loadData = async () => {
    if (!datasetId) return;

    setStatus('loading');

    try {
      // 1. 获取授权状态（包含已关联的项目信息）
      const authStatusRes = await getGbaseAuthStatus(datasetId);
      const { gbase_integration } = authStatusRes.data;

      // 2. 只有 authorized: false 时才显示未授权状态
      if (!gbase_integration.authorized) {
        setStatus('no_auth');
        return;
      }

      // 3. authorized: true，检查是否已有关联的 Project
      if (gbase_integration.has_projects && gbase_integration.projects.length > 0) {
        // 有已关联的项目，直接使用 status 接口返回的数据
        setAssociatedProject(gbase_integration.projects[0]);
        setStatus('has_data_selected');
      } else {
        // 4. 没有已关联项目，获取全部可用的 Project 列表
        try {
          const projectsRes = await getGbaseProjects({
            current_page: 1,
            page_size: PAGE_SIZE,
          });
          const { projects: projectsList, paginator: paginatorData } = projectsRes.data as GetGbaseProjectsResponse;
          setProjects(projectsList || []);
          setPaginator(paginatorData);
          setStatus('has_data_not_selected');
        } catch (error) {
          console.error('获取项目列表失败:', error);
          setStatus('has_data_not_selected');
          setProjects([]);
        }
      }
    } catch (error) {
      console.error('检查授权状态失败:', error);
      setStatus('no_auth');
    }
  };

  // 加载指定页的项目列表
  const loadProjects = async (page: number) => {
    if (!datasetId) return;

    setLoading(true);
    try {
      const projectsRes = await getGbaseProjects({
        current_page: page,
        page_size: PAGE_SIZE,
      });
      const { projects: projectsList, paginator: paginatorData } = projectsRes.data as GetGbaseProjectsResponse;
      setProjects(projectsList || []);
      setPaginator(paginatorData);
    } catch (error) {
      console.error('获取项目列表失败:', error);
      setProjects([]);
    } finally {
      setLoading(false);
    }
  };

  // 处理分页切换
  const handlePageChange = (page: number) => {
    loadProjects(page);
  };

  // 弹窗打开时加载数据
  useEffect(() => {
    if (opened && datasetId) {
      loadData();
    }
  }, [opened, datasetId]);

  // 发起授权
  const handleAuthorize = async () => {
    try {
      // 保存当前页面的相对路径（pathname + search），授权成功后跳回
      const redirectPath = window.location.pathname + window.location.search;
      localStorage.setItem('gbase_auth_redirect_url', redirectPath);

      const res = await gbaseAuthorize({
        dataset_id: datasetId,
      });

      if (res.data.authorization_url) {
        // 直接跳转到授权 URL，后端返回的就是正确的地址
        window.location.href = res.data.authorization_url;
      }
    } catch (error) {
      console.error('发起授权失败:', error);
    }
  };

  // 关联 Project
  const handleAssociate = async () => {
    if (!selectedUuid || !datasetId) return;

    setLoading(true);
    try {
      const res = await associateGbaseProjects({
        dataset_id: datasetId,
        project_uuids: [selectedUuid],
      });

      // 显示成功提示
      if (res.data.associated_projects && res.data.associated_projects.length > 0) {
        const projectTitle = res.data.associated_projects[0].title;
        showNotification({
          title: t('create_v2.integrations.data_sources.gbase_project.associate_success_title'),
          message: t('create_v2.integrations.data_sources.gbase_project.associate_success_message', {
            name: projectTitle,
          }),
          color: 'green',
          icon: <IconCheck size={16} />,
        });
      }

      // 刷新集成列表数据
      if (routeDatasetId) {
        await fetchRules({ datasetId: routeDatasetId, botId: routeBotId });
      }

      onClose();
    } catch (error) {
      console.error('关联 Project 失败:', error);
      showNotification({
        title: t('create_v2.integrations.data_sources.gbase_project.associate_error_title'),
        message: t('create_v2.integrations.data_sources.gbase_project.associate_error_message'),
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  };

  // 解除关联
  const handleDisassociate = async () => {
    if (!associatedProject || !datasetId) return;

    setLoading(true);
    try {
      await disassociateGbaseProject(datasetId, associatedProject.uuid);
      setDeleteConfirmOpened(false);
      // 重新加载数据
      await loadData();
      // 刷新集成列表数据
      if (routeDatasetId) {
        await fetchRules({ datasetId: routeDatasetId, botId: routeBotId });
      }
    } catch (error) {
      console.error('解除关联失败:', error);
    } finally {
      setLoading(false);
    }
  };

  // 获取弹窗标题
  const getModalTitle = () => {
    switch (status) {
      case 'loading':
        return '';
      case 'no_auth':
        return t('create_v2.integrations.data_sources.gbase_project.modal_no_auth_title');
      case 'has_data_not_selected':
        return t('create_v2.integrations.data_sources.gbase_project.modal_select_title');
      case 'has_data_selected':
        return t('create_v2.integrations.data_sources.gbase_project.modal_selected_title');
      default:
        return '';
    }
  };

  // 渲染未授权/无数据状态
  const renderNoAuthContent = () => (
    <Stack gap="md" align="center" className="py-4">
      <div className="w-20 h-20 flex items-center justify-center text-gray-400">
        <IconMessageCircle size={64} stroke={1.5} />
      </div>
      <Text size="lg" fw={600} className="text-gray-800">
        {t('create_v2.integrations.data_sources.gbase_project.no_data_title')}
      </Text>
      <Text size="sm" className="text-gray-600 text-center">
        {t('create_v2.integrations.data_sources.gbase_project.no_data_desc')}
      </Text>
      <Button onClick={handleAuthorize} className="mt-4">
        {t('create_v2.integrations.data_sources.gbase_project.connect_gbase')}
      </Button>

      {/* 技术说明 */}
      <div className="w-full mt-4 p-4 bg-gray-50 border-l-[3px] border-gray-400 rounded">
        <Text size="xs" fw={600} className="text-gray-700 mb-2">
          {t('create_v2.integrations.data_sources.gbase_project.tech_note_title')}
        </Text>
        <Text size="xs" className="text-gray-500 leading-relaxed">
          {t('create_v2.integrations.data_sources.gbase_project.tech_note_content_no_auth')}
        </Text>
      </div>
    </Stack>
  );

  // 渲染有数据未选择状态
  const renderSelectContent = () => (
    <div>
      <Group className="mb-4" gap={0}>
        <Text size="sm" className="text-gray-600">
          {t('create_v2.integrations.data_sources.gbase_project.select_tip')}
        </Text>
      </Group>

      <ScrollArea.Autosize mah={300} className="mb-4">
        {projects.length === 0 ? (
          // 空状态
          <Stack align="center" gap="md" className="py-8">
            <div className="w-16 h-16 flex items-center justify-center text-gray-300">
              <IconMessageCircle size={64} stroke={1.5} />
            </div>
            <Text size="sm" c="dimmed" ta="center">
              {t('create_v2.integrations.data_sources.gbase_project.no_projects')}
            </Text>
          </Stack>
        ) : (
          <ul className="flex flex-wrap gap-3">
            {projects.map((item) => (
              <li
                key={item.uuid}
                className="border border-gray-200 rounded-md p-3 w-full sm:w-[48%] cursor-pointer hover:bg-violet-50"
                onClick={() => setSelectedUuid(item.uuid)}
              >
                <Group justify="space-between" wrap="nowrap" className="h-full">
                  <Group wrap="nowrap" className="grow overflow-hidden">
                    <div className="flex-shrink-0">
                      <SvgDataset />
                    </div>
                    <div className="grow overflow-hidden">
                      <Tooltip label={item.title} withinPortal>
                        <Text fw={500} className="line-clamp-2">
                          {item.title}
                        </Text>
                      </Tooltip>
                      <Text size="sm" className="text-gray-400 truncate">
                        {item.my_role} · {item.item_count} items
                      </Text>
                    </div>
                  </Group>
                  <Checkbox checked={selectedUuid === item.uuid} onChange={() => {}} />
                </Group>
              </li>
            ))}
          </ul>
        )}
      </ScrollArea.Autosize>

      {/* 分页组件 */}
      {paginator.pages > 1 && (
        <Group justify="center" className="mb-4">
          <Pagination total={paginator.pages} value={paginator.currentPage} onChange={handlePageChange} size="sm" />
        </Group>
      )}

      <Group justify="flex-end" gap="sm">
        <Button size="sm" variant="outline" color="gray" onClick={onClose}>
          {t('alert_cancel')}
        </Button>
        <Button size="sm" onClick={handleAssociate} disabled={!selectedUuid} loading={loading}>
          {t('embeddings.dataset.add_modal.add_btn')}
        </Button>
      </Group>
    </div>
  );

  // 渲染有数据且已选择状态
  const renderSelectedContent = () => (
    <Stack gap="md">
      {/* 顶部说明文字 */}
      <Text size="sm" className="text-gray-600">
        {t('create_v2.integrations.data_sources.gbase_project.config_tip')}
      </Text>

      {/* 已选项目展示 */}
      <div className="bg-slate-50 border border-indigo-100 rounded-md p-5">
        <Group justify="space-between" wrap="nowrap">
          <Group wrap="nowrap" className="flex-1 min-w-0">
            <div className="w-6 h-6 flex-shrink-0 text-green-500">
              <IconCheck size={24} />
            </div>
            <div className="overflow-hidden">
              <Text fw={500} className="text-gray-800">
                {associatedProject?.title}
              </Text>
              <Text size="sm" className="text-gray-500 mt-1">
                {t('create_v2.integrations.data_sources.gbase_project.connector_desc')}
              </Text>
            </div>
          </Group>
          <Button size="sm" variant="light" color="red" onClick={() => setDeleteConfirmOpened(true)}>
            {t('create_v2.integrations.data_sources.gbase_project.disconnect_btn')}
          </Button>
        </Group>
      </div>

      {/* 技术说明 */}
      <div className="p-4 bg-gray-50 border-l-[3px] border-slate-400 rounded">
        <Text size="xs" className="text-gray-700 leading-relaxed">
          <span style={{ fontWeight: 600 }}>{t('create_v2.integrations.data_sources.gbase_project.rule_change')}</span>
          {t('create_v2.integrations.data_sources.gbase_project.rule_change_content')}
          <br />
          <span style={{ fontWeight: 600 }}>
            {t('create_v2.integrations.data_sources.gbase_project.operation_impact')}
          </span>
          {t('create_v2.integrations.data_sources.gbase_project.operation_impact_content')}
          <br />
          <span style={{ fontWeight: 600 }}>
            {t('create_v2.integrations.data_sources.gbase_project.common_issues')}
          </span>
          {t('create_v2.integrations.data_sources.gbase_project.common_issues_content')}
        </Text>
      </div>

      {/* 底部按钮 */}
      <Group justify="flex-end" gap="sm">
        <Button size="sm" variant="outline" color="gray" onClick={onClose}>
          {t('alert_cancel')}
        </Button>
        <Button size="sm" onClick={onClose}>
          {t('create_v2.integrations.data_sources.gbase_project.save_config')}
        </Button>
      </Group>
    </Stack>
  );

  // 根据状态渲染不同内容
  const renderContent = () => {
    switch (status) {
      case 'loading':
        return (
          <Stack align="center" py="xl">
            <Text size="sm" c="dimmed">
              {t('common.loading')}
            </Text>
          </Stack>
        );
      case 'no_auth':
        return renderNoAuthContent();
      case 'has_data_not_selected':
        return renderSelectContent();
      case 'has_data_selected':
        return renderSelectedContent();
      default:
        return null;
    }
  };

  return (
    <>
      <Modal opened={opened} onClose={onClose} title={getModalTitle()} centered size="lg">
        {renderContent()}
      </Modal>

      {/* 断开确认弹窗 */}
      <Modal
        opened={deleteConfirmOpened}
        onClose={() => setDeleteConfirmOpened(false)}
        title={t('create_v2.integrations.data_sources.gbase_project.disconnect_confirm_title')}
        centered
        size="sm"
      >
        <Stack gap="md">
          <Text size="sm">
            {t('create_v2.integrations.data_sources.gbase_project.disconnect_confirm_content', {
              name: associatedProject?.title,
            })}
          </Text>
          <Group justify="flex-end" gap="sm">
            <Button size="sm" variant="outline" color="gray" onClick={() => setDeleteConfirmOpened(false)}>
              {t('alert_cancel')}
            </Button>
            <Button size="sm" color="red" onClick={handleDisassociate} loading={loading}>
              {t('create_v2.integrations.data_sources.gbase_project.confirm_disconnect')}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
};
